import { Button, Typography } from "@blofin/blofin-ui";
import { useStoreState } from "../store/useStore";
import { useTranslations } from "use-intl";
import AssetsPopper from "../AssetsPopper";
import OverviewPopper from "../OverviewPopper";
import NewUserBanner from "./NewUserBanner";
import GiftIcon from "@/nav-localized/assets/header/nav/gift.svg?react";

import NotificationPopper from "../NotificationPopper";
import DepositDrawer from "../DepositDrawer";
import NotificationIcon from "@/nav-localized/assets/header/notification.svg?react";
import { stylesText } from "../styles";
import clsx from "clsx";
import { FuturesModeSwitch } from "../FuturesActions";
import { SpotModeSwitch } from "../SpotActions";

// 配置
import { showNewUserBanner } from "@/nav-localized/config";

/** Pages Router 或 App Router：无 `window.next` 时用 location 构造等价字段 */
const getRouterCompat = () => {
  const r = typeof window !== "undefined" ? window.next?.router : undefined;
  if (r) return r;
  if (typeof window === "undefined") return null;

  const query: Record<string, string> = {};
  new URLSearchParams(window.location.search).forEach((v, k) => {
    query[k] = v;
  });

  return {
    notNextjs: undefined as undefined,
    basePath: "",
    asPath: `${window.location.pathname}${window.location.search}` || "/",
    query: query as Record<string, string | string[] | undefined>
  };
};

const User = () => {
  const { hideSignUpAndLogIn, isLogin, theme, localeLink } = useStoreState();

  const t = useTranslations("header");

  /**
   * 检查当前路由是否为 task-center
   */
  const isTaskCenterRoute = () => {
    const router = getRouterCompat();
    if (!router) return false;

    // 检查路由是否包含 task-center
    return router?.asPath?.includes?.("task-center");
  };

  const getCurrentUrl = (url: string) => {
    const router = getRouterCompat();
    const notNextjs = router?.notNextjs;
    if (notNextjs) {
      return `${localeLink}/${url}`;
    }

    if (!router) {
      return `${localeLink}/${url}`;
    }

    const basePath = router.basePath && router.basePath !== "/" ? router.basePath : "";
    const asPath = router.asPath || "/";

    const buildRedirectPath = () => {
      if (asPath === "/") {
        return `${basePath}${localeLink}/`.replace(/\/{2,}/g, "/");
      }

      const normalizedAsPath = asPath.startsWith("/") ? asPath : `/${asPath}`;
      // 只对 pathname 做斜杠压缩，保留 query/hash 中合法的 //（如嵌套 URL 参数）
      const splitIdx = normalizedAsPath.search(/[?#]/);
      const pathPart = splitIdx === -1 ? normalizedAsPath : normalizedAsPath.slice(0, splitIdx);
      const qsPart = splitIdx === -1 ? "" : normalizedAsPath.slice(splitIdx);
      return `${basePath}${localeLink}${pathPart}`.replace(/\/{2,}/g, "/") + qsPart;
    };

    if (router.asPath === "/") {
      return `${localeLink}/${url}?redirect=${encodeURIComponent(buildRedirectPath())}`;
    }

    const referralRaw = router.query?.referral_code;
    const referralStr =
      typeof referralRaw === "string"
        ? referralRaw
        : Array.isArray(referralRaw)
          ? referralRaw[0]
          : "";
    const refCode =
      referralStr &&
      (router.asPath.includes(`?referral_code=`) || router.asPath.includes(`&referral_code=`))
        ? `referral_code=${referralStr}&`
        : "";

    return `${localeLink}/${url}?${refCode}redirect=${encodeURIComponent(buildRedirectPath())}`;
  };

  if (isLogin === null) {
    return null;
  }

  return (
    <div className="flex items-center h-full">
      {!isLogin ? (
        hideSignUpAndLogIn ? null : (
          <>
            <a className="max-[600px]:hidden" href={getCurrentUrl("login")}>
              <Button
                variant="text"
                size="small"
                className="font-medium"
                id="header_login_in"
                theme={theme}
              >
                <Typography
                  theme={theme}
                  variant="body3"
                  className={clsx(
                    "font-medium text-[14px]",
                    theme === "dark" ? "hover:text-dark-primary" : "hover:text-light-primary"
                  )}
                >
                  {t("login")}
                </Typography>
              </Button>
            </a>
            <a href={getCurrentUrl("register")}>
              <Button
                variant="primary"
                size="small"
                className="font-medium w-auto h-[32px] !rounded-full text-[14px] leading-[20px]"
                id="header_sign_up"
                theme={theme}
              >
                <GiftIcon className="mr-2 block h-[18px] w-[18px] shrink-0" />
                {t("signup")}
              </Button>
            </a>
            <a
              className={clsx(
                "max-[600px]:hidden",
                "h-full ml-[24px] flex items-center",
                stylesText({ mode: theme }),
                theme === "dark" ? "hover:text-dark-primary" : "hover:text-light-primary"
              )}
              href={getCurrentUrl("login")}
            >
              <NotificationIcon className={clsx("w-5 h-5")} />
            </a>
          </>
        )
      ) : (
        <div className="flex h-full items-center">
          {isLogin && !isTaskCenterRoute() && showNewUserBanner && (
            <NewUserBanner className="mr-[16px]" localeLink={localeLink} />
          )}
          <DepositDrawer />
          <AssetsPopper />
          <FuturesModeSwitch />
          <SpotModeSwitch />
          <OverviewPopper />
          <NotificationPopper />
        </div>
      )}
    </div>
  );
};

export default User;
